import React, { useEffect, useState } from 'react';
import { useAppContext } from '@/context/AppContext';
import FieldGroup from '@/components/FieldGroup';
import SectionTitle from '@/components/SectionTitle';
import Dropdown from '@/components/Dropdown';
import UploadField from '@/components/UploadField';
import Separator from '@/components/Separator';
import dropboxClient from '@/utils/dropboxClient';
import logger from '@/utils/logger';

const languageOptions = [
    { value: 'en-AU', label: 'English (Australian)' },
    { value: 'en-US', label: 'English (US)' },
    { value: 'en-UK', label: 'English (UK)' },
    { value: 'fil-PH', label: 'Filipino / Tagalog' },
    { value: 'hi-IN', label: 'Hindi' },
    { value: 'zh-CN', label: 'Chinese (Simplified)' },
    { value: 'zh-TW', label: 'Chinese (Traditional)' },
    { value: 'ja-JP', label: 'Japanese' },
    { value: 'ko-KR', label: 'Korean' },
    { value: 'ar-SA', label: 'Arabic (RTL)' },
    { value: 'ps-AF', label: 'Pashto (RTL)' },
    { value: 'fa-AF', label: 'Dari' },
    { value: 'si-LK', label: 'Sinhala' },
    { value: 'ta-LK', label: 'Tamil' },
    { value: 'sq-AL', label: 'Albanian' },
    { value: 'es-ES', label: 'Spanish' },
    { value: 'other', label: 'Other' },
];

const rtlLanguages = ['ar-SA', 'ps-AF'];

const slugify = (text) =>
  text
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');


function Step1Identity() {
  const { projectDraft, dispatch } = useAppContext();
  const { siteTitle = '', primaryLanguage = 'en-AU' } = projectDraft;
  const [uploads, setUploads] = useState({
    logo: { status: 'idle', file: null },
    favicon: { status: 'idle', file: null },
    touchIcon: { status: 'idle', file: null },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch({ type: 'UPDATE_PROJECT_DRAFT', payload: { [name]: value } });
  };
  
  const handleFileChange = (file, type) => {
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = async () => {
      const base64Data = reader.result.split(',')[1];
      setUploads(prev => ({ ...prev, [type]: { status: 'uploading', file } }));
      try {
        const uniqueFileName = `${Date.now()}-${file.name}`;
        const result = await dropboxClient.uploadFile(`/promptly_assets/${uniqueFileName}`, base64Data);
        setUploads(prev => ({ ...prev, [type]: { status: 'success', file } }));
        const assetKey = `${type}Path`; // e.g., logoPath
        dispatch({
          type: 'UPDATE_PROJECT_DRAFT',
          payload: { assets: { ...projectDraft.assets, [assetKey]: result.path_display } },
        });
      } catch (err) {
        logger.error(`Failed to upload ${type}`, err);
        setUploads(prev => ({ ...prev, [type]: { status: 'error', file } }));
      }
    };
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target
    dispatch({ type: 'UPDATE_PROJECT_DRAFT', payload: { [name]: checked }})
  }
  
  useEffect(() => {
    if (siteTitle) {
      dispatch({ type: 'UPDATE_PROJECT_DRAFT', payload: { siteSlug: slugify(siteTitle) } });
    }
  }, [siteTitle, dispatch]);

  useEffect(() => {
    let spellingVariant = 'N/A';
    if (primaryLanguage.startsWith('en-AU') || primaryLanguage.startsWith('en-UK')) {
      spellingVariant = 'AU/UK';
    } else if (primaryLanguage.startsWith('en-US')) {
      spellingVariant = 'US';
    }
    const isRtl = rtlLanguages.includes(primaryLanguage);
    dispatch({
      type: 'UPDATE_PROJECT_DRAFT',
      payload: { spellingVariant, rtlLayout: isRtl },
    });
  }, [primaryLanguage, dispatch]);

  return (
    <div>
      <SectionTitle title="Step 1: Identity" subtitle="Define the core identity of your project." />
        <FieldGroup label="Developer Name">
          <input
            type="text"
            name="developerName"
            value={projectDraft.developerName || ''}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </FieldGroup>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FieldGroup label="Site Title">
          <input
            type="text"
            name="siteTitle"
            value={projectDraft.siteTitle || ''}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </FieldGroup>
        <FieldGroup label="Site Slug">
          <input
            type="text"
            name="siteSlug"
            value={projectDraft.siteSlug || ''}
            readOnly
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100 sm:text-sm"
          />
        </FieldGroup>
      </div>

      <Separator />

      <SectionTitle title="Project Assets" subtitle="Upload your branding assets to Dropbox." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <UploadField label="Logo" onFileSelect={file => handleFileChange(file, 'logo')} status={uploads.logo.status} fileName={uploads.logo.file?.name} />
        <UploadField label="Favicon" onFileSelect={file => handleFileChange(file, 'favicon')} status={uploads.favicon.status} fileName={uploads.favicon.file?.name} />
        <UploadField label="Touch Icon" onFileSelect={file => handleFileChange(file, 'touchIcon')} status={uploads.touchIcon.status} fileName={uploads.touchIcon.file?.name} />
      </div>

      <Separator />
      
      <SectionTitle title="Language & Locale" subtitle="Configure language and regional settings." />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FieldGroup label="Primary Language">
            <Dropdown options={languageOptions} name="primaryLanguage" value={projectDraft.primaryLanguage} onChange={handleChange} />
        </FieldGroup>
        <FieldGroup label="Spelling Variant">
          <input
            type="text"
            value={projectDraft.spellingVariant || 'N/A'}
            readOnly
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100 sm:text-sm"
          />
        </FieldGroup>
      </div>
      <div className="mt-4 flex items-center">
        <input type="checkbox" name="rtlLayout" id="rtlLayout" checked={projectDraft.rtlLayout || false} onChange={handleCheckboxChange} className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
        <label htmlFor="rtlLayout" className="ml-2 block text-sm text-gray-900">Enable RTL Layout</label>
      </div>
    </div>
  );
}

export default Step1Identity;